import { useState, useEffect } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, HeartPulse, Download, FileText } from 'lucide-react';
import { downloadHRDocument, formatDateShort } from './hrUtils';

export const BenefitsTab = () => {
  const [benefits, setBenefits] = useState(null);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    Promise.all([hrAPI.getBenefits(), hrAPI.listDocuments({ category: 'benefits_doc' })])
      .then(([b, d]) => { setBenefits(b.data); setDocs(d.data); })
      .catch(() => setBenefits({ overview: '', plans: [] }));
  }, []);

  if (!benefits) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  const plans = benefits.plans || [];

  return (
    <div className="space-y-6" data-testid="hr-benefits-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800 text-lg flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-rose-500" /> Benefits Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {benefits.overview ? (
            <p className="text-sm text-slate-600 whitespace-pre-wrap" data-testid="hr-benefits-overview">{benefits.overview}</p>
          ) : (
            <p className="text-sm text-slate-400">Benefits information has not been published yet. Contact HR for details.</p>
          )}
        </CardContent>
      </Card>

      {plans.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hr-benefits-plans">
          {plans.map((p, i) => (
            <Card key={i} className="bg-white border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-medium text-slate-800">{p.name}</p>
                  {p.type && <Badge variant="secondary" className="capitalize">{p.type}</Badge>}
                </div>
                {p.provider && <p className="text-xs text-slate-500 mb-1">Provider: {p.provider}</p>}
                {p.details && <p className="text-sm text-slate-600 whitespace-pre-wrap">{p.details}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {docs.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-slate-800 text-lg">Benefits Documents</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-rose-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.title}</p>
                    <p className="text-xs text-slate-400">{formatDateShort(d.created_at)}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => downloadHRDocument(d)}>
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
